import type { CheckoutRequest } from "@/types/woocommerce.types";
import { normalize_order_creation_error } from "../payment-plugin-registry";
import type { PaymentProcessingResult } from "../payment-plugin-registry";
import { create_order } from "../../services/woocommerce/order-service";
import { PAYMENT_ERROR_CODES } from "@/lib/constants/payment-error-constants";
import {
  log_gateway_event,
  report_gateway_error,
} from "@/lib/observability/sentry-reporting";

declare global {
  interface Window {
    PaytikoSdk?: any;
    PAYTIKO_CASHIER_DISPLAY_MODE?: {
      IFRAME: string;
      OVERLAY?: string;
      POPUP?: string;
      FULLSCREEN?: string;
    };
  }
}

const get_order_amount = async (order_id: number): Promise<number> => {
  try {
    const res = await fetch(`/api/woocommerce/order?order_id=${order_id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) {
      log_gateway_event(
        "error",
        "paytiko",
        "get_order_amount",
        "Failed to fetch order amount",
        { status: res.status, statusText: res.statusText }
      );
      return 0;
    }
    const data = await res.json();
    const amount = Number(data?.order_amount);
    return isNaN(amount) ? 0 : amount;
  } catch (err) {
    report_gateway_error({
      error: err,
      gateway: "paytiko",
      step: "get_order_amount",
      message: "Error fetching order amount",
      severity: "error",
    });
    return 0;
  }
};

const load_paytiko_sdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.PaytikoSdk) return resolve();
    const script = document.createElement("script");
    script.src = "https://core.paytiko.com/cdn/js/sdk/paytiko-sdk.1.0.min.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Paytiko SDK"));
    document.head.appendChild(script);
  });
};

const open_cashier_modal = (): void => {
  const overlay = document.getElementById(
    "paytiko-overlay"
  ) as HTMLDivElement | null;
  const container = document.getElementById(
    "paytiko_iframe_container"
  ) as HTMLDivElement | null;

  if (overlay && container) {
    overlay.classList.remove("hidden");
    overlay.classList.add("flex");
    const close_btn = overlay.querySelector(
      '[data-close="paytiko-overlay"]'
    ) as HTMLButtonElement | null;
    if (close_btn) {
      close_btn.onclick = () => {
        overlay.classList.add("hidden");
        overlay.classList.remove("flex");
      };
    }
    return;
  }

  const new_overlay = document.createElement("div");
  new_overlay.id = "paytiko-overlay";
  new_overlay.className =
    "fixed inset-0 z-[9999] flex items-center justify-center bg-black/60";

  const new_container = document.createElement("div");
  new_container.className =
    "relative h-[80%] w-[90%] max-w-[900px] overflow-hidden rounded-lg bg-white";

  const close_btn = document.createElement("button");
  close_btn.type = "button";
  close_btn.innerText = "×";
  close_btn.setAttribute("aria-label", "Close cashier");
  close_btn.className =
    "absolute right-3 top-2 cursor-pointer text-3xl leading-none text-gray-700 hover:text-gray-900";
  close_btn.onclick = () => {
    new_overlay.remove();
  };

  const iframe_container = document.createElement("div");
  iframe_container.id = "paytiko_iframe_container";
  iframe_container.className = "h-full w-full";

  new_container.appendChild(close_btn);
  new_container.appendChild(iframe_container);
  new_overlay.appendChild(new_container);
  document.body.appendChild(new_overlay);
};

export const paytiko_payment_handler = async (
  order_data: CheckoutRequest
): Promise<PaymentProcessingResult> => {
  const payload: CheckoutRequest = {
    ...order_data,
    payment_method: "paytiko",
    payment_data: [{ key: "payment_method", value: "paytiko" }],
  };

  const order_result = await create_order({ order_data: payload });

  log_gateway_event(
    "info",
    "paytiko",
    "payment_handler",
    "Order creation result",
    { order_result }
  );

  if (!order_result?.order_id) {
    return normalize_order_creation_error({
      gateway: "paytiko",
      result: { code: order_result.code, message: order_result.message },
    });
  }

  const order_id = Number(order_result.order_id);
  const amount = await get_order_amount(order_id);
  if (!amount || amount <= 0) {
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      message: "Could not determine order amount",
    };
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const endpoint = `${origin}/api/paytiko/checkout`;
  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      order_id,
      amount,
      currency: "USD",
      billing_details: {
        email: order_data.billing_address?.email,
        first_name: order_data.billing_address?.first_name,
        last_name: order_data.billing_address?.last_name,
        phone: order_data.billing_address?.phone,
        address_1: order_data.billing_address?.address_1,
        address_2: order_data.billing_address?.address_2,
        city: order_data.billing_address?.city,
        state: order_data.billing_address?.state,
        postcode: order_data.billing_address?.postcode,
        country: order_data.billing_address?.country,
      },
      cashier_description: `Order ${order_id}`,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    log_gateway_event(
      "error",
      "paytiko",
      "checkout_api",
      "Checkout API failed",
      { status: res.status, errorText: text }
    );
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      message: "Failed to initialize Paytiko cashier",
    };
  }

  const data = await res.json();
  const session_token: string | undefined = data?.paytikoSessionToken;
  if (!session_token) {
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      message: "Missing Paytiko session token",
    };
  }

  try {
    await load_paytiko_sdk();
  } catch (err) {
    report_gateway_error({
      error: err,
      gateway: "paytiko",
      step: "load_sdk",
      message: "SDK failed to load",
      severity: "error",
    });
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      message: "Could not load Paytiko SDK",
    };
  }

  open_cashier_modal();

  if (!window.PaytikoSdk?.cashier?.invoke) {
    log_gateway_event(
      "error",
      "paytiko",
      "sdk_invoke",
      "SDK cashier.invoke missing"
    );
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      message: "Paytiko SDK not available",
    };
  }

  try {
    window.PaytikoSdk.cashier.invoke({
      environment: "PRODUCTION",
      orderId: String(order_id),
      sessionToken: session_token,
      iframeContainerSelector: "#paytiko_iframe_container",
      displayMode: window.PAYTIKO_CASHIER_DISPLAY_MODE?.IFRAME ?? "IFRAME",
      locale: "en-US",
    });
  } catch (err) {
    report_gateway_error({
      error: err,
      gateway: "paytiko",
      step: "sdk_invoke",
      message: "SDK cashier.invoke error",
      severity: "error",
    });
    return {
      success: false,
      error_code: PAYMENT_ERROR_CODES.PAYMENT_PROCESSING_ERROR,
      message: "Failed to start Paytiko cashier",
    };
  }

  return {
    success: true,
    order_id,
    message: "Paytiko cashier opened",
  };
};
